"use client";

import { ReactNode, useEffect, useMemo } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { toast } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { AppErrorBoundary, isChunkLoadError, tryChunkReloadOnce } from "@/components/AppErrorBoundary";
import { VersionRefreshPrompt } from "@/components/VersionRefreshPrompt";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { PaletteProvider } from "@/components/theme/PaletteProvider";
import { PresenceProvider } from "@/hooks/usePresence";
import { WrenchAssistant } from "@/components/wrench/WrenchAssistant";
import { reportClientError } from "@/lib/error-report";

let toastErrorPatched = false;

function patchToastError() {
  if (toastErrorPatched) return;
  toastErrorPatched = true;
  const original = toast.error.bind(toast);
  toast.error = ((msg: Parameters<typeof toast.error>[0], opts?: Parameters<typeof toast.error>[1]) => {
    const message =
      typeof msg === "string"
        ? msg
        : msg instanceof Error
          ? msg.message
          : (() => {
              try {
                return String(msg);
              } catch {
                return "unknown toast error";
              }
            })();
    reportClientError({ source: "toast", message });
    return original(msg, opts);
  }) as typeof toast.error;
}

function extractErrorMessage(value: unknown): string {
  if (value == null) return "";
  if (value instanceof Error) return value.message || value.name || "Error";
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof (value as { message?: unknown }).message === "string") {
    return (value as { message: string }).message;
  }
  try {
    return String(value);
  } catch {
    return "unknown error";
  }
}

function extractErrorStack(value: unknown): string | null {
  if (value instanceof Error && typeof value.stack === "string") return value.stack;
  if (value && typeof value === "object") {
    const maybeStack = (value as { stack?: unknown }).stack;
    if (typeof maybeStack === "string") return maybeStack;
  }
  return null;
}

function SpeedInsightsGate() {
  const enabled = useMemo(() => {
    if (process.env.NODE_ENV !== "production") {
      return false;
    }
    if (typeof window === "undefined") {
      return false;
    }
    return window.location.hostname !== "127.0.0.1" && window.location.hostname !== "localhost";
  }, []);

  return enabled ? <SpeedInsights /> : null;
}

function PresenceGate({ children }: { children: ReactNode }) {
  const enabled = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    if (!window.isSecureContext) {
      return false;
    }
    return window.location.hostname !== "127.0.0.1" && window.location.hostname !== "localhost";
  }, []);

  return enabled ? <PresenceProvider>{children}</PresenceProvider> : <>{children}</>;
}

function GlobalErrorGuards() {
  useEffect(() => {
    patchToastError();

    const isMaskedUrlStack = (value: unknown) => {
      if (!value) return false;
      if (typeof value === "string") {
        return value.includes("webkit-masked-url://hidden/");
      }
      if (value instanceof Error) {
        return typeof value.stack === "string" && value.stack.includes("webkit-masked-url://hidden/");
      }
      if (typeof value === "object" && value !== null) {
        const maybeStack = (value as { stack?: unknown }).stack;
        return typeof maybeStack === "string" && maybeStack.includes("webkit-masked-url://hidden/");
      }
      return false;
    };

    const isMaskedUrlFilename = (value: unknown) =>
      typeof value === "string" && value.startsWith("webkit-masked-url://hidden/");

    const isPortalBodyError = (reason: unknown) => {
      if (reason == null) return false;
      const msg =
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : typeof (reason as { message?: unknown }).message === "string"
              ? (reason as { message: string }).message
              : "";
      return msg.includes("document.body") || msg.includes("body.appendChild");
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason == null || isMaskedUrlStack(event.reason) || isPortalBodyError(event.reason)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      // A lazy import that fails inside an event handler never reaches the error
      // boundary — it lands here as an unhandled rejection, so the user stays
      // stuck until they refresh by hand. Same stale-deploy cause, so reuse the
      // boundary's shared one-shot reload. If the session already spent it, fall
      // through and report normally rather than reloading again.
      if (isChunkLoadError(event.reason) && tryChunkReloadOnce()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      reportClientError({
        source: "unhandledrejection",
        message: extractErrorMessage(event.reason),
        stack: extractErrorStack(event.reason),
      });
    };

    const handleWindowError = (event: ErrorEvent) => {
      if (isPortalBodyError(event.error) || isPortalBodyError(event.message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const isUndefinedBrowserError =
        event.message === "undefined" &&
        (!event.error || isMaskedUrlStack(event.error)) &&
        (!event.filename || isMaskedUrlFilename(event.filename));

      if (
        isUndefinedBrowserError ||
        (isMaskedUrlFilename(event.filename) &&
          (!event.error || isMaskedUrlStack(event.error) || event.message === "undefined"))
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      // Resource-load failures (img/script/link/font 404s, ad-block/extension
      // blocks) fire on window in the capture phase with the failing element as
      // event.target and no usable error/message. Detail-less cross-origin
      // "Script error." events look the same. Both carry zero diagnostic value,
      // so drop them instead of persisting opaque "window error" rows.
      const target = event.target as EventTarget | null;
      const isResourceLoadError = target != null && target !== window && target instanceof Element;
      const message = extractErrorMessage(event.error) || event.message || "";
      const isOpaque = !message || message === "Script error." || message === "undefined";
      if (isResourceLoadError || isOpaque) {
        return;
      }

      reportClientError({
        source: "window",
        message,
        stack: extractErrorStack(event.error),
      });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection, { capture: true });
    window.addEventListener("error", handleWindowError, { capture: true });
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection, { capture: true });
      window.removeEventListener("error", handleWindowError, { capture: true });
    };
  }, []);

  return null;
}

export function Providers({ children }: { children: ReactNode }) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
          },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <PaletteProvider>
        <TooltipProvider>
          <AuthProvider>
            <PresenceGate>
              <AppErrorBoundary>
                <GlobalErrorGuards />
                {children}
                <WrenchAssistant />
                <Toaster />
                <Sonner />
                <VersionRefreshPrompt />
                <SpeedInsightsGate />
              </AppErrorBoundary>
            </PresenceGate>
          </AuthProvider>
        </TooltipProvider>
        </PaletteProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
